'use client';

/*
 * `use client` — Next.js (RSC) uchun.
 *
 * Bu komponentlar React hook'laridan foydalanadi (`useState`, `useId`,
 * `useRef`), demak ular server komponenti bo'la olmaydi. Direktiva
 * bo'lmasa `apps/web` buildi yiqiladi.
 *
 * Vite ilovalari (`miniapp`, `admin`) uchun bu qator hech narsa
 * qilmaydi — u yerda hammasi baribir klientda ishlaydi.
 */
import { type KeyboardEvent, type ReactNode, useId, useRef, useState } from 'react';
import {
  type Locale,
  type OrderListItem,
  type ProductListItem,
  type PublicStage,
  type ShipmentResponse,
} from '@aps/contracts';
import { telUrl, telegramUrl } from '../lib/contact.js';
import { cx, pickText } from '../lib/format.js';
import { IconCart, IconChevronRight, IconSend } from './icons.js';
import { availability, comingSoonLabel } from '../lib/stock.js';
import {
  PUBLIC_STAGE_ORDER,
  orderListSummary,
  shipmentSummary,
  stageIndex,
  stageLabel,
  trackerSteps,
} from '../lib/order-view.js';
import { PriceTag } from './primitives.js';

/**
 * Domen komponentlari.
 *
 * Hujjatdagi MATN QOIDALARI shu yerda kod darajasida majburlanadi:
 * qoldiq "qayerda bor" deb ko'rsatiladi, moslik "mos keladi" deb
 * aytilmaydi, mijozga ichki status ko'rsatilmaydi.
 */

/* =============================== Tile =============================== */

interface TileBase {
  /** Kategoriya kaliti — rangni `data-cat` orqali beradi. */
  categoryKey: string;
  label: string;
  count?: number;
  /** Berilsa `count` o'rniga shu matn ko'rsatiladi (masalan "945 mahsulot"). */
  countText?: string;
  icon?: ReactNode;
}

/**
 * Plitka: TUGMA yoki HAVOLA.
 *
 * Mini App'da bu tugma — u ichki navigatsiya (URL yo'q). Web saytda
 * esa HAVOLA bo'lishi SHART: qidiruv roboti `onClick` ni bosmaydi va
 * tugmadan hech qayerga o'tolmaydi. Ikkalasi bitta komponent, chunki
 * ko'rinish bir xil bo'lishi kerak.
 */
export type TileProps = TileBase &
  ({ href: string; onSelect?: never } | { href?: never; onSelect: () => void });

export function Tile({
  categoryKey,
  label,
  count,
  countText,
  icon,
  ...action
}: TileProps): ReactNode {
  /*
   * Rasm uyasi: qat'iy 56x56 (CSS `.aps-tile__icon`). Hozir ichida SVG
   * ikonka; maketdagi 3D rasm kelganda `icon` o'rniga `<img>` uzatiladi
   * (`object-fit: contain` CSS da tayyor) — plitka o'lchami o'zgarmaydi.
   */
  const body = (
    <>
      <span className="aps-tile__icon">{icon}</span>
      <span className="aps-tile__label">{label}</span>
      {countText !== undefined ? (
        <span className="aps-tile__count">{countText}</span>
      ) : count === undefined ? null : (
        <span className="aps-tile__count">{count}</span>
      )}
    </>
  );

  if (action.href !== undefined) {
    return (
      <a className="aps-tile" data-cat={categoryKey} href={action.href}>
        {body}
      </a>
    );
  }

  return (
    <button type="button" className="aps-tile" data-cat={categoryKey} onClick={action.onSelect}>
      {body}
    </button>
  );
}

/* ============================ ProductCard ============================ */

interface ProductCardBase {
  product: ProductListItem;
  locale: Locale;
  /** Mijoz hududidagi do'kon(lar) — qoldiq matnini aniqlaydi. */
  homeShopIds?: readonly string[];
  /** Rasmi yo'q tovar uchun kategoriya ikonkasi. */
  fallbackIcon?: ReactNode;
  /**
   * Savat tugmasi (44x44, o'ng pastda). Berilmasa tugma chizilmaydi.
   * FAQAT `onSelect` variantida: `<a>` ichida `<button>` bo'lmaydi (HTML
   * noto'g'ri), web saytda esa savat yo'q — u yerda faqat havola.
   */
  onAddToCart?: (product: ProductListItem) => void;
  /** Tugma a11y nomi ("Savatga qo'shish") — tarjima ekrandan keladi. */
  addToCartLabel?: string;
  /**
   * Ekran sababi bo'yicha o'chirish (mehmon, so'rov ketmoqda). Tugagan va
   * "Tez kunda" tovar uchun kartaning o'zi tugmani o'chiradi.
   */
  addToCartDisabled?: boolean;
}

/**
 * Tovar kartochkasi: TUGMA yoki HAVOLA.
 *
 * Web saytda havola bo'lishi SHART — qidiruv roboti tugmani bosmaydi
 * va tovar sahifasini umuman topmaydi. Aynan shu sayt SEO uchun
 * qurilyapti, ya'ni bu ixtiyoriy emas.
 */
export type ProductCardProps = ProductCardBase &
  (
    | { href: string; onSelect?: never }
    | { href?: never; onSelect: (product: ProductListItem) => void }
  );

export function ProductCard({
  product,
  locale,
  homeShopIds,
  fallbackIcon,
  onAddToCart,
  addToCartLabel,
  addToCartDisabled = false,
  ...action
}: ProductCardProps): ReactNode {
  const stock = availability(product, {
    ...(homeShopIds === undefined ? {} : { homeShopIds }),
    locale,
  });
  const name = pickText(product.nameUz, product.nameRu, locale);

  /*
   * Savat tugmasi kartaning ICHIDA, lekin karta bosilishi (tovarni ochish)
   * ishga tushmasligi uchun sichqoncha VA klaviatura hodisalari to'xtatiladi:
   * aks holda tugmada Enter bosish kartaning `onKeyDown` iga o'tib, tovarni
   * ochib yuborardi.
   */
  const stop = (event: { stopPropagation: () => void }): void => {
    event.stopPropagation();
  };
  const cartButton =
    onAddToCart === undefined || action.href !== undefined ? null : (
      <button
        type="button"
        className="aps-product__cart"
        // Nom bilan: skrinrider ro'yxatda "Savatga qo'shish" ni takrorlab aytmasin.
        aria-label={addToCartLabel === undefined ? name : `${addToCartLabel}: ${name}`}
        disabled={addToCartDisabled || product.comingSoon || stock.kind === 'out'}
        onClick={(event) => {
          event.stopPropagation();
          onAddToCart(product);
        }}
        onKeyDown={stop}
        onKeyUp={stop}
      >
        <IconCart size={22} />
      </button>
    );

  const body = (
    <>
      <span className="aps-product__media">
        {product.imageUrl === null ? (
          // Rasmi yo'q tovar bo'sh kvadrat bo'lib turmaydi — kategoriya
          // ikonkasi ko'rsatiladi (docs/06 → 5-savol).
          <span className="aps-product__fallback">{fallbackIcon}</span>
        ) : (
          <img src={product.imageUrl} alt={name} loading="lazy" width={400} height={400} />
        )}
      </span>
      {/*
        Matn qismi ALOHIDA o'ralgan: demoda media maydoni kartochka
        chetigacha yetadi va faqat matn qismida ichki bo'shliq bor
        (`demo/avtopartshop-demo.html` → `.pim` / `.pbd`).
      */}
      <span className="aps-product__body">
        <span className="aps-product__name">{name}</span>
        {/* Ikkinchi qator: brend (ro'yxat javobida mashina maydoni yo'q). */}
        <span className="aps-product__brand">{product.brandName}</span>
        <span className="aps-product__foot">
          <span className="aps-product__buy">
            {product.comingSoon ? (
              // Narx ko'rsatilmaydi: u 0 bo'lishi mumkin va "0 so'm" yolg'on bo'lardi.
              <span className="aps-price aps-price--soon">{comingSoonLabel(locale)}</span>
            ) : (
              <>
                <PriceTag
                  value={product.price}
                  oldValue={product.oldPrice}
                  locale={locale}
                  size="sm"
                />
                <span className={cx('aps-stock', `aps-stock--${stock.kind}`)}>{stock.short}</span>
              </>
            )}
          </span>
          {cartButton}
        </span>
      </span>
    </>
  );

  if (action.href !== undefined) {
    return (
      <a className="aps-product" data-cat={product.categoryIconKey ?? undefined} href={action.href}>
        {body}
      </a>
    );
  }

  /*
   * `<button>` EMAS, `role="button"` li `<div>`.
   *
   * Karta ichida blok tuzilma (media + matn) bor va uni flex ustun qilish
   * kerak. Safari `<button display:flex>` ichidagi bolalarni kartaning
   * kengligigacha CHO'ZMAYDI (ichki o'ram ularni mazmuniga qarab
   * siqadi): rasm kartaning yarmicha qoladi, uzun narx qatori esa matnni
   * kartadan chiqarib yuboradi (iPhone XS Max'da o'lchangan). Chromium'da
   * bunday nosozlik yo'q, shuning uchun u lokal testda ko'rinmaydi.
   * `<div>` da bu qiyinchilik umuman yo'q.
   *
   * Klaviatura `<button>` dagidek: Enter bosilganda, Probel qo'yib
   * yuborilganda ishlaydi.
   */
  const select = (): void => {
    action.onSelect(product);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="aps-product"
      data-cat={product.categoryIconKey ?? undefined}
      onClick={select}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          select();
        } else if (event.key === ' ') {
          // Sahifa pastga siljimasin; amalning o'zi tugma qo'yib yuborilganda.
          event.preventDefault();
        }
      }}
      onKeyUp={(event) => {
        if (event.key === ' ') select();
      }}
    >
      {body}
    </div>
  );
}

/* ============================== Banner ============================== */

export interface BannerProps {
  title: string;
  subtitle?: string;
  badge?: string;
  categoryKey?: string;
  action?: ReactNode;
  /** Admin panelda berilgan rasm. Bo'lmasa `icon` yoki toza rang ko'rsatiladi. */
  imageUrl?: string;
  /** Rasm bo'lmaganda o'ng tomonda xira dekorativ ikonka. */
  icon?: ReactNode;
  /** Birinchi (ekranda ko'rinadigan) banner rasmi kechiktirilmaydi — LCP. */
  priority?: boolean;
  /** Bosiladigan bannerda ko'rinadigan "Ko'rish →" yorlig'i (faqat `onSelect` bilan chiqadi). */
  ctaLabel?: string;
  /**
   * Berilsa banner BOSILADIGAN bo'ladi: butun maydonni qoplovchi tugma (sarlavha ekran
   * o'quvchiga sarlavha bo'lib qoladi). Surish (scroll) bosish hisoblanmaydi.
   */
  onSelect?: () => void;
  /**
   * Web: banner HAVOLA bo'ladi (`<a>`), tugma emas — qidiruv roboti `onClick` ni
   * bosmaydi. `onSelect` bilan birga berilsa `href` ustun.
   */
  href?: string;
}

export function Banner({
  title,
  subtitle,
  badge,
  categoryKey,
  action,
  imageUrl,
  icon,
  priority = false,
  ctaLabel,
  onSelect,
  href,
}: BannerProps): ReactNode {
  return (
    <section
      className="aps-banner"
      data-cat={categoryKey}
      data-media={imageUrl !== undefined ? 'image' : icon !== undefined ? 'art' : 'none'}
    >
      {imageUrl === undefined ? (
        icon === undefined ? null : (
          <span className="aps-banner__art" aria-hidden="true">
            {icon}
          </span>
        )
      ) : (
        <img
          className="aps-banner__img"
          src={imageUrl}
          alt=""
          width={320}
          height={160}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
        />
      )}
      {/*
        Yorliq, sarlavha va izoh har bannerda O'Z qatorida turadi: maydon bo'sh bo'lsa ham joy
        saqlanadi (yashirin bo'sh element), shunda karuselda slaydlar o'zgarganda matn sakramaydi.
      */}
      {badge === undefined || badge === '' ? (
        <span className="aps-banner__badge aps-banner__badge--empty" aria-hidden="true" />
      ) : (
        <span className="aps-banner__badge">{badge}</span>
      )}
      <h2 className="aps-banner__title">{title}</h2>
      {subtitle === undefined || subtitle === '' ? (
        <p className="aps-banner__subtitle aps-banner__subtitle--empty" aria-hidden="true" />
      ) : (
        <p className="aps-banner__subtitle">{subtitle}</p>
      )}
      {action}
      {(onSelect === undefined && href === undefined) || ctaLabel === undefined ? null : (
        <span className="aps-banner__cta" aria-hidden="true">
          {ctaLabel}
          <span className="aps-banner__cta-arrow">→</span>
        </span>
      )}
      {href !== undefined ? (
        <a className="aps-banner__hit" aria-label={title} href={href} />
      ) : onSelect === undefined ? null : (
        <button type="button" className="aps-banner__hit" aria-label={title} onClick={onSelect} />
      )}
    </section>
  );
}

/* ============================ ProductGallery ============================ */

export interface GalleryImage {
  src: string;
  alt: string;
}

export interface ProductGalleryProps {
  images: readonly GalleryImage[];
  /** Rasm bo'lmaganda ko'rsatiladi (kategoriya ikonkasi). */
  fallback: ReactNode;
  /** Ekran o'quvchi uchun galereya nomi. */
  label: string;
  /**
   * "Oldingi" va "Keyingi" strelka yorliqlari. IKKALASI berilsa strelkalar chiqadi (sichqonchali
   * qurilma uchun: barmoq bilan surish u yerda yo'q). Mini App bermaydi — u yerda barmoq yetarli.
   */
  prevLabel?: string;
  nextLabel?: string;
  /** Rasm ostida miniatyuralar qatori (web tovar sahifasi). Berilsa nuqtalar chizilmaydi. */
  thumbnails?: boolean;
}

/**
 * Tovar rasmlari galereyasi.
 *
 * Native gorizontal scroll + `scroll-snap`: Telegram WebView inertiyasini qo'lda qayta
 * yasash undan yomonroq ishlaydi (banner karuseli bilan bir xil qaror). JS joriy rasm
 * raqamini ("1 / 4") kuzatadi va strelka/nuqta/miniatyura/klaviatura bosilganda kerakli rasmga
 * silliq suradi: sichqonchali kompyuterda faqat scroll yetarli emas edi (rasmlarni
 * almashtirib bo'lmasdi). Bitta rasm bo'lsa hech qanday boshqaruv chizilmaydi.
 * Birinchi rasm `eager` — u sahifaning asosiy (LCP) elementi.
 */
export function ProductGallery({
  images,
  fallback,
  label,
  prevLabel,
  nextLabel,
  thumbnails = false,
}: ProductGalleryProps): ReactNode {
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  if (images.length === 0) {
    return (
      <div className="aps-product__media">
        <span className="aps-product__fallback">{fallback}</span>
      </div>
    );
  }

  const sync = (): void => {
    const node = scroller.current;
    if (node === null || node.clientWidth === 0) return;
    const index = Math.floor(node.scrollLeft / node.clientWidth + 0.5);
    setActive(Math.min(images.length - 1, Math.max(0, index)));
  };

  const go = (index: number): void => {
    const node = scroller.current;
    const target = Math.min(images.length - 1, Math.max(0, index));
    setActive(target);
    if (node === null) return;
    const left = target * node.clientWidth;
    // `scrollTo` bo'lmagan muhitda (eski WebView, jsdom) to'g'ridan-to'g'ri suriladi.
    if (typeof node.scrollTo === 'function') node.scrollTo({ left, behavior: 'smooth' });
    else node.scrollLeft = left;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (images.length < 2) return;
    if (event.key === 'ArrowLeft') go(active - 1);
    else if (event.key === 'ArrowRight') go(active + 1);
    else return;
    event.preventDefault();
  };

  const many = images.length > 1;
  const arrows = many && prevLabel !== undefined && nextLabel !== undefined;
  const slideLabel = (index: number): string => `${String(index + 1)} / ${String(images.length)}`;

  return (
    <>
      <div className="aps-gallery" role="group" aria-roledescription="carousel" aria-label={label}>
        <div
          ref={scroller}
          className="aps-gallery__track"
          tabIndex={0}
          onScroll={sync}
          onKeyDown={onKeyDown}
        >
          {images.map((image, index) => (
            <div
              className="aps-gallery__slide"
              key={`${String(index)}-${image.src}`}
              role="group"
              aria-roledescription="slide"
              aria-label={slideLabel(index)}
            >
              <img
                src={image.src}
                alt={image.alt}
                width={1200}
                height={1200}
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
                draggable={false}
              />
            </div>
          ))}
        </div>
        {arrows ? (
          <>
            <button
              type="button"
              className="aps-gallery__nav aps-gallery__nav--prev"
              aria-label={prevLabel}
              disabled={active === 0}
              onClick={() => {
                go(active - 1);
              }}
            >
              <IconChevronRight size={20} />
            </button>
            <button
              type="button"
              className="aps-gallery__nav aps-gallery__nav--next"
              aria-label={nextLabel}
              disabled={active === images.length - 1}
              onClick={() => {
                go(active + 1);
              }}
            >
              <IconChevronRight size={20} />
            </button>
          </>
        ) : null}
        {many ? (
          <>
            {thumbnails ? null : (
              <span className="aps-gallery__dots">
                {images.map((image, index) => (
                  <button
                    type="button"
                    key={`${String(index)}-${image.src}`}
                    className={index === active ? 'aps-gallery__dot is-active' : 'aps-gallery__dot'}
                    aria-label={slideLabel(index)}
                    aria-current={index === active}
                    onClick={() => {
                      go(index);
                    }}
                  />
                ))}
              </span>
            )}
            <span className="aps-gallery__count" aria-hidden="true">
              {active + 1} / {images.length}
            </span>
          </>
        ) : null}
      </div>
      {many && thumbnails ? (
        <div className="aps-gallery__thumbs" role="group" aria-label={label}>
          {images.map((image, index) => (
            <button
              type="button"
              key={`${String(index)}-${image.src}`}
              className="aps-gallery__thumb"
              aria-label={slideLabel(index)}
              aria-current={index === active}
              onClick={() => {
                go(index);
              }}
            >
              <img src={image.src} alt="" width={72} height={72} loading="lazy" decoding="async" />
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}

/* ========================= ActiveOrderCard ========================= */

export interface ActiveOrderCardProps {
  item: OrderListItem;
  locale: Locale;
  onSelect: () => void;
}

/**
 * Bosh ekrandagi faol buyurtma.
 *
 * BOSQICHLI indikator, FOIZLI chiziq EMAS — va bu ataylab.
 *
 * Demoda foiz `(FLOW.indexOf(status)+1)/FLOW.length` dan hisoblanadi.
 * Xitoy yetkazishida jo'natma bojxonada uch hafta turishi mumkin va
 * 60% da qotib qolgan chiziq mijozga YOLG'ON gapiradi: u har kuni
 * qarab, hech narsa o'zgarmaganini ko'radi va ishonchni yo'qotadi.
 *
 * Bosqichlar esa rost: ular sanaladi, nomlanadi va har biri
 * MIJOZGA ko'rinadigan holat (`publicStage`), ichki status emas
 * (`docs/02-MAHSULOT.md`). Ko'rinishi demodagi kartochkaday, ma'nosi
 * to'g'ri.
 */
export interface ContactCardProps {
  title: string;
  /** Mijozning muammosiga qaratilgan qisqa matn ("Avtomobilingizga mosligini aniqlashtiring"). */
  description?: string | undefined;
  /** Ish vaqti kabi ikkilamchi qator. */
  hours?: string | undefined;
  /** Admin panelda kiritilgan xom qiymat; noto'g'ri bo'lsa tugma chiqmaydi. */
  phone: string;
  telegram: string;
  writeLabel: string;
  /** Boshqa karta (FormSection) ichida: o'z ramkasi va ichki bo'shlig'i bo'lmaydi. */
  embedded?: boolean;
  /** Telegram suhbati shu matn bilan ochiladi (operator mavzuni darrov ko'radi). */
  telegramMessage?: string | undefined;
  /**
   * Telegram havolasini ilova ichida ochish (Mini App). Bermasangiz oddiy havola.
   * Bosilganda sahifa almashmasligi uchun `preventDefault` qilinadi.
   */
  onOpenTelegram?: (url: string) => void;
}

/**
 * Operator bilan aloqa kartochkasi — KONTEKSTLI: Profil, buyurtma va tovar sahifasida
 * bir xil komponent, har joyda o'z sarlavhasi va xabari bilan.
 *
 * Raqam bosiladigan `tel:` havola (qo'ng'iroqni mijoz o'zi qiladi; Telegram ichida `tel:`
 * tugmasi ishonchsiz, shuning uchun alohida qo'ng'iroq tugmasi yo'q). Yagona tugma — Telegram.
 * Ikkala qiymat ham noto'g'ri yoki bo'sh bo'lsa kartochka umuman chizilmaydi.
 */
export function ContactCard({
  title,
  description,
  hours,
  phone,
  telegram,
  writeLabel,
  telegramMessage,
  embedded = false,
  onOpenTelegram,
}: ContactCardProps): ReactNode {
  const tel = telUrl(phone);
  const tg = telegramUrl(telegram, telegramMessage);
  if (tel === null && tg === null) return null;

  return (
    <section className={cx('aps-contact', embedded && 'aps-contact--embedded')} aria-label={title}>
      <div className="aps-contact__text">
        <span className="aps-contact__title">{title}</span>
        {description === undefined || description === '' ? null : (
          <span className="aps-contact__hint">{description}</span>
        )}
        {tel === null ? null : (
          <a className="aps-contact__phone" href={tel}>
            {phone.trim()}
          </a>
        )}
        {hours === undefined || hours === '' ? null : (
          <span className="aps-contact__hint">{hours}</span>
        )}
      </div>
      <div className="aps-contact__actions">
        {tg === null ? null : (
          <a
            className="aps-contact__btn"
            href={tg}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => {
              if (onOpenTelegram === undefined) return;
              event.preventDefault();
              onOpenTelegram(tg);
            }}
          >
            <IconSend size={20} />
            <span>{writeLabel}</span>
          </a>
        )}
      </div>
    </section>
  );
}

export function ActiveOrderCard({ item, locale, onSelect }: ActiveOrderCardProps): ReactNode {
  const summary = orderListSummary(item, locale);
  const current = stageIndex(item.publicStage);

  return (
    <button type="button" className="aps-active-order" data-cat="access" onClick={onSelect}>
      <span className="aps-active-order__head">
        <span className="aps-active-order__number">{item.number}</span>
        <span className="aps-active-order__count">
          {locale === 'RU'
            ? `${String(item.itemCount)} товара`
            : `${String(item.itemCount)} ta tovar`}
        </span>
        <span className="aps-chip aps-chip--accent">{stageLabel(item.publicStage, locale)}</span>
      </span>

      {/*
        Chipda BOSQICH turibdi, shuning uchun bu qatorda u
        TAKRORLANMAYDI — faqat MUDDAT. Ikkalasida bir xil so'z
        turgani ma'lumot bermaydi, joy egallaydi.

        Muddat yo'q bo'lsa (hali qabul qilingan, ETA qo'yilmagan)
        qator bo'sh qolmaydi: bosqich matni ko'rsatiladi.
      */}
      <span className="aps-active-order__eta">
        {item.slowestEtaTo === null
          ? summary.sub
          : `${locale === 'RU' ? 'Ожидается' : 'Kutilmoqda'} ${summary.headline}`}
      </span>

      {/*
        Har bosqich uchun bitta segment. `CANCELLED` bu yerga
        tushmaydi — u bosqich emas, alohida holat.
      */}
      <span className="aps-active-order__steps" aria-hidden>
        {PUBLIC_STAGE_ORDER.map((stage, index) => (
          <span
            key={stage}
            className="aps-active-order__step"
            data-done={index <= current ? '' : undefined}
          />
        ))}
      </span>
    </button>
  );
}

/* ============================ GarageCard ============================ */

export interface GarageVehicle {
  makeName: string;
  modelName: string;
  year: number;
}

export interface GarageCardProps {
  /**
   * Faol avtomobil. `null` — garaj bo'sh.
   *
   * Ilgari bu uchta alohida maydon edi va bo'sh holat uchun ekran
   * `makeName=""`, `year={0}` uzatardi. Natijada foydalanuvchi
   * "Mashinangizni qo'shing 0" va "Mashinangizni qo'shing 0 uchun
   * tanlov" degan matnni ko'rgan. Bo'sh holat ENDI ANIQ.
   */
  vehicle: GarageVehicle | null;
  /** Garaj bo'sh bo'lgandagi matnlar — tarjima ekrandan keladi. */
  emptyTitle: string;
  emptyHint: string;
  locale: Locale;
  onSelect: () => void;
  icon?: ReactNode;
}

/**
 * Garaj kartochkasi.
 *
 * Matn "Tracker 2023 uchun" ko'rinishida — "mos keladi" DEYILMAYDI,
 * chunki moslik `searchTags` orqali aniqlanadi va u ehtimollik
 * (docs/02-MAHSULOT.md → "Garaj").
 */
export function GarageCard({
  vehicle,
  emptyTitle,
  emptyHint,
  locale,
  onSelect,
  icon,
}: GarageCardProps): ReactNode {
  const title =
    vehicle === null
      ? emptyTitle
      : `${vehicle.makeName} ${vehicle.modelName} ${String(vehicle.year)}`;
  const hint =
    vehicle === null
      ? emptyHint
      : locale === 'RU'
        ? 'Подборка для этой машины'
        : 'Shu mashina uchun tanlov';

  return (
    <button type="button" className="aps-garage" onClick={onSelect}>
      {icon}
      <span className="aps-option__body">
        <span className="aps-garage__title">{title}</span>
        <span className="aps-garage__hint">{hint}</span>
      </span>
    </button>
  );
}

/* =========================== StatusTracker =========================== */

export interface StatusTrackerProps {
  stage: PublicStage;
  type: 'SHOP' | 'CHINA';
  locale: Locale;
}

/**
 * To'liq treker — UCHINCHI QATLAM.
 *
 * Mijozga faqat `publicStage` ko'rsatiladi. Ichki status
 * ("CUSTOMS_DECLARED") unga hech narsa demaydi va HECH QACHON
 * chiqarilmaydi.
 */
export function StatusTracker({ stage, type, locale }: StatusTrackerProps): ReactNode {
  const steps = trackerSteps(stage, locale, { type });
  if (steps.length === 0) return null;

  return (
    <ol className="aps-tracker">
      {steps.map((step) => (
        <li key={step.stage} className="aps-tracker__step" data-state={step.state}>
          <span className="aps-tracker__marker" aria-hidden="true" />
          <span
            className="aps-tracker__label"
            aria-current={step.state === 'current' ? 'step' : undefined}
          >
            {step.label}
          </span>
        </li>
      ))}
    </ol>
  );
}

/* ============================ ShipmentRow ============================ */

export interface ShipmentRowProps {
  shipment: Pick<ShipmentResponse, 'id' | 'publicStage' | 'etaFrom' | 'etaTo' | 'items'>;
  locale: Locale;
  onSelect: () => void;
}

/**
 * Buyurtma ichidagi jo'natma qatori — IKKINCHI QATLAM.
 *
 * Sana va BIR SO'ZLI holat. Progress bar YO'Q: u har qatorda takrorlanib,
 * ro'yxatni shovqinga to'ldiradi (docs/02-MAHSULOT.md).
 */
export function ShipmentRow({ shipment, locale, onSelect }: ShipmentRowProps): ReactNode {
  const summary = shipmentSummary(shipment, locale);
  const count = shipment.items.length;
  const itemsText = locale === 'RU' ? `${String(count)} товара` : `${String(count)} ta tovar`;

  return (
    <button type="button" className="aps-shipment" onClick={onSelect}>
      <span className="aps-shipment__body">
        <span className="aps-shipment__eta">
          {summary.eta === '' ? summary.status : summary.eta}
        </span>
        <span className="aps-shipment__status">
          {itemsText}
          {summary.eta === '' ? '' : ` · ${summary.status}`}
        </span>
      </span>
    </button>
  );
}

/* ============================== KpiCard ============================== */

export interface KpiCardProps {
  label: string;
  value: string;
  /**
   * Raqamning MA'NOSI: "0 tasi yangi", "marja 30%".
   *
   * Yalang'och raqam qaror qildirmaydi — "3 ta buyurtma" ko'p mi,
   * kam mi? Izoh shu savolga javob beradi.
   */
  hint?: string;
  /** Kategoriya rangi — kartochkalar bir-biridan ajralib tursin. */
  categoryKey?: string;
  /** Foizdagi o'zgarish. Musbat — yashil, manfiy — qizil. */
  deltaPercent?: number;
}

export function KpiCard({
  label,
  value,
  hint,
  categoryKey,
  deltaPercent,
}: KpiCardProps): ReactNode {
  return (
    <div className="aps-kpi" data-cat={categoryKey}>
      <span className="aps-kpi__label">{label}</span>
      <span className="aps-kpi__value">{value}</span>
      {hint === undefined ? null : <span className="aps-kpi__hint">{hint}</span>}
      {deltaPercent === undefined ? null : (
        <span className={deltaPercent >= 0 ? 'aps-kpi__delta--up' : 'aps-kpi__delta--down'}>
          {deltaPercent >= 0 ? '↑' : '↓'} {Math.abs(deltaPercent)}%
        </span>
      )}
    </div>
  );
}

/* ============================= DataTable ============================= */

export interface DataTableColumn<T> {
  key: string;
  header: string;
  /** Raqamli ustun o'ngga tekislanadi va `tabular-nums` oladi. */
  numeric?: boolean;
  render: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  caption: string;
  empty?: ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  empty,
}: DataTableProps<T>): ReactNode {
  if (rows.length === 0 && empty !== undefined) return <>{empty}</>;

  return (
    <table className="aps-table">
      {/* Jadval nomi skrinrider uchun majburiy — admin panelda kritik. */}
      <caption className="aps-table__caption">{caption}</caption>
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column.key} scope="col">
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={rowKey(row)}>
            {columns.map((column) => (
              <td key={column.key} data-numeric={column.numeric === true ? 'true' : undefined}>
                {column.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ============================= QueueGroup ============================= */

export interface QueueGroupProps {
  label: string;
  count: number;
  /** ETA o'tib ketgan guruh — alohida ajratiladi. */
  urgent?: boolean;
  onSelect: () => void;
  icon?: ReactNode;
}

/**
 * Ish navbati guruhi.
 *
 * Admin asosiy ekrani buyurtma ro'yxati emas, VAZIFA TURI bo'yicha
 * navbat: operator bir xil turdagi vazifalarni ketma-ket bajaradi
 * (docs/02-MAHSULOT.md).
 */
export function QueueGroup({
  label,
  count,
  urgent = false,
  onSelect,
  icon,
}: QueueGroupProps): ReactNode {
  return (
    <button
      type="button"
      className={cx('aps-queue', urgent && 'aps-queue--urgent')}
      onClick={onSelect}
    >
      {icon}
      <span className="aps-option__body">{label}</span>
      <span className="aps-queue__count">{count}</span>
    </button>
  );
}

/* ============================ FormSection ============================ */

export interface FormSectionProps {
  title: string;
  hint?: string;
  children: ReactNode;
}

export function FormSection({ title, hint, children }: FormSectionProps): ReactNode {
  const titleId = useId();
  return (
    <section className="aps-form-section" aria-labelledby={titleId}>
      <h3 id={titleId} className="aps-form-section__title">
        {title}
      </h3>
      {hint === undefined ? null : <p className="aps-form-section__hint">{hint}</p>}
      {children}
    </section>
  );
}

/* =========================== ImageDropzone =========================== */

export interface ImageDropzoneProps {
  label: string;
  hint?: string;
  accept?: string;
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

/**
 * Rasm yuklash maydoni.
 *
 * Klaviatura bilan ham ishlaydi: `<label>` yashirin `<input type=file>`
 * ni boshqaradi, shuning uchun Tab va Enter tabiiy ishlaydi.
 */
export function ImageDropzone({
  label,
  hint,
  accept = 'image/*',
  onFiles,
  disabled = false,
}: ImageDropzoneProps): ReactNode {
  const inputId = useId();
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <label
      htmlFor={inputId}
      className="aps-dropzone"
      data-dragging={dragging ? 'true' : 'false'}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => {
        setDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        if (disabled) return;
        onFiles([...event.dataTransfer.files]);
      }}
    >
      <span>{label}</span>
      {hint === undefined ? null : <span className="aps-dropzone__hint">{hint}</span>}
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        multiple
        disabled={disabled}
        hidden
        onChange={(event) => {
          onFiles([...(event.target.files ?? [])]);
          // Bir xil faylni qayta tanlash ham hodisa bersin.
          if (inputRef.current !== null) inputRef.current.value = '';
        }}
      />
    </label>
  );
}

/* ========================= LangSwitch / Theme ========================= */

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedProps<T extends string> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (next: T) => void;
  label: string;
}

function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: SegmentedProps<T>): ReactNode {
  return (
    <div className="aps-segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="aps-segmented__item"
          aria-pressed={option.value === value}
          onClick={() => {
            onChange(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export interface LangSwitchProps {
  value: Locale;
  onChange: (next: Locale) => void;
}

/**
 * Til almashtirgich.
 *
 * Til Telegram `language_code` dan AVTOMATIK olinadi va so'ralmaydi;
 * bu almashtirgich faqat sozlamalarda, qo'lda o'zgartirish uchun
 * (docs/02-MAHSULOT.md → "Ikki til").
 */
export function LangSwitch({ value, onChange }: LangSwitchProps): ReactNode {
  return (
    <Segmented
      label="Til"
      value={value}
      onChange={onChange}
      options={[
        { value: 'UZ', label: 'O‘zbekcha' },
        { value: 'RU', label: 'Русский' },
      ]}
    />
  );
}

export const THEME_NAMES = ['dark', 'light', 'race'] as const;

export type ThemeName = (typeof THEME_NAMES)[number];

export interface ThemeToggleProps {
  value: ThemeName;
  onChange: (next: ThemeName) => void;
  labels: Record<ThemeName, string> & { group: string };
}

export function ThemeToggle({ value, onChange, labels }: ThemeToggleProps): ReactNode {
  return (
    <Segmented
      label={labels.group}
      value={value}
      onChange={onChange}
      options={THEME_NAMES.map((name) => ({ value: name, label: labels[name] }))}
    />
  );
}
